<template>
  <div class="pvtprediction-dashboard">
    <EmbeddedBreadcrumbBar
      :items="breadcrumbItems"
      home-mode="pvt"
      @pvt-navigate="navigatePvtPath"
    />
    <div v-if="error" class="error-message">
      {{ error }}
    </div>
    <iframe
      v-else
      :src="iframeSrc"
      class="prediction-iframe"
      ref="predictionIframe"
      @load="handleIframeLoad"
    ></iframe>
  </div>
  <div v-if="isLoading" class="loader-container-eda">
    <div class="loader">
      <div class="spinner"></div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from "vuex";
import EmbeddedBreadcrumbBar from "@/components/layout/Common/EmbeddedBreadcrumbBar.vue";
import {
  notifyIframeDarkMode,
  notifyIframeTenant,
  notifyIframePvtRoute,
  getIframeOrigin,
  PVT_ROUTE_CHANGED_MESSAGE,
} from "@/utils/iframeDarkMode";
import {
  buildPvtBreadcrumbItems,
  normalizePvtIframePath,
} from "@/utils/pvtIframeRoute";

export default {
  name: "PVTPrediction",
  components: {
    EmbeddedBreadcrumbBar,
  },
  data() {
    return {
      error: null,
      isLoading: true,
      iframeSrc: null,
      pvtIframePath: "/",
    };
  },
  computed: {
    ...mapGetters("darkMode", ["isDarkMode"]),
    breadcrumbItems() {
      return buildPvtBreadcrumbItems(this.pvtIframePath);
    },
    pvtOrigin() {
      return process.env.VUE_APP_PVT_PREDICTION_URL;
    },
    routeTenantId() {
      return this.$route.path.split("/")[1] || "";
    },
  },
  mounted() {
    window.addEventListener("message", this.handleIframeMessage);
    this.refreshIframeSrc();
    this.isLoading = true;
    setTimeout(() => {
      this.isLoading = false;
    }, 3000);
  },
  methods: {
    buildIframeUrl() {
      const token = localStorage.getItem("authToken");
      const tenantId = this.$route.path.split("/")[1];
      if (!token || !tenantId) {
        this.error = "Authentication required";
        this.$router.push("/login");
        return null;
      }

      const darkMode = this.isDarkMode ? "true" : "false";
      const origin = String(this.pvtOrigin || "").replace(/\/$/, "");
      const parentOrigin = encodeURIComponent(window.location.origin);
      return `${origin}/?token=${token}&tenantId=${tenantId}&darkMode=${darkMode}&parentOrigin=${parentOrigin}`;
    },
    refreshIframeSrc() {
      const url = this.buildIframeUrl();
      if (url) {
        this.iframeSrc = url;
      }
    },
    syncIframeDarkMode() {
      this.$nextTick(() => {
        notifyIframeDarkMode(this.$refs.predictionIframe, this.isDarkMode, this.pvtOrigin);
      });
    },
    syncIframeTenant(tenantId) {
      this.$nextTick(() => {
        notifyIframeTenant(
          this.$refs.predictionIframe,
          tenantId || this.routeTenantId,
          this.pvtOrigin
        );
      });
    },
    handleIframeLoad() {
      const iframe = this.$refs.predictionIframe;
      if (iframe) {
        this.error = null;
        notifyIframeDarkMode(iframe, this.isDarkMode, this.pvtOrigin);
        notifyIframeTenant(iframe, this.routeTenantId, this.pvtOrigin);
        setTimeout(() => {
          this.isLoading = false;
        }, 3000);
      }
    },
    handleIframeMessage(event) {
      if (event.origin !== getIframeOrigin(this.pvtOrigin)) {
        return;
      }
      const data = event.data;
      if (!data) {
        return;
      }
      if (data.type === PVT_ROUTE_CHANGED_MESSAGE) {
        this.pvtIframePath = normalizePvtIframePath(data.pathname);
        return;
      }
      if (data.type === "TOKEN_EXPIRED") {
        this.handleTokenExpiration();
      }
    },
    navigatePvtPath(pathname) {
      const path = normalizePvtIframePath(pathname);
      this.pvtIframePath = path;
      notifyIframePvtRoute(this.$refs.predictionIframe, path, this.pvtOrigin);
    },
    handleTokenExpiration() {
      this.$store.dispatch("tenantId/logoutUser");
      this.error = "Your session has expired. Please login again.";
    },
  },
  watch: {
    isDarkMode() {
      this.syncIframeDarkMode();
    },
    routeTenantId(newId, oldId) {
      if (newId && newId !== oldId) {
        this.syncIframeTenant(newId);
      }
    },
  },
  beforeUnmount() {
    window.removeEventListener("message", this.handleIframeMessage);
  },
};
</script>
